import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip
} from '@mui/material';
import {
  Description,
  History,
  CheckCircle,
  Warning,
  Info
} from '@mui/icons-material';
import { Document, Page, PDFViewer } from '@react-pdf/renderer';

interface DocumentPreviewProps {
  open: boolean;
  document: any;
  onClose: () => void;
}

export const DocumentPreview: React.FC<DocumentPreviewProps> = ({
  open,
  document,
  onClose
}) => {
  const [tab, setTab] = useState(0);

  if (!document) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{document.name}</Typography>
          <Chip
            icon={document.status === 'VALID' ? <CheckCircle /> : <Warning />}
            label={document.status}
            color={document.status === 'VALID' ? 'success' : 'warning'}
          />
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={tab} onChange={(e, v) => setTab(v)}>
            <Tab label="Preview" />
            <Tab label="Details" />
            <Tab label="History" />
          </Tabs>
        </Box>

        {tab === 0 && (
          <Paper elevation={0} sx={{ height: '60vh', overflow: 'auto' }}>
            {document.url && (
              <object
                data={document.url}
                type="application/pdf"
                width="100%"
                height="100%"
              >
                <iframe
                  src={document.url}
                  width="100%"
                  height="100%"
                  style={{ border: 'none' }}
                  title="PDF Preview"
                >
                  <p>Your browser does not support PDFs. Please download the PDF to view it.</p>
                </iframe>
              </object>
            )}
          </Paper>
        )}

        {tab === 1 && (
          <List>
            <ListItem>
              <ListItemIcon><Description /></ListItemIcon>
              <ListItemText
                primary="Document Type"
                secondary={document.type}
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><Info /></ListItemIcon>
              <ListItemText
                primary="State"
                secondary={document.state}
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><Info /></ListItemIcon>
              <ListItemText
                primary="Upload Date"
                secondary={new Date(document.uploadDate).toLocaleString()}
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><Info /></ListItemIcon>
              <ListItemText
                primary="Last Validated"
                secondary={new Date(document.lastValidated).toLocaleString()}
              />
            </ListItem>
          </List>
        )}

        {tab === 2 && (
          <List>
            {document.history?.map((event: any, index: number) => (
              <ListItem key={index}>
                <ListItemIcon><History /></ListItemIcon>
                <ListItemText
                  primary={event.action}
                  secondary={new Date(event.timestamp).toLocaleString()}
                />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => window.open(document.url, '_blank')}
        >
          Download
        </Button>
      </DialogActions>
    </Dialog>
  );
}; 